/**
 * NCR Edit Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useNCREdit hook.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft, Loader2, FileWarning, RotateCcw,
  Lock, Unlock, Trash2, Download,
} from 'lucide-react';
import NCRDeleteDialog from '../components/quality/NCRDeleteDialog';
import NCRLifecyclePanel from './ncr-edit/NCRLifecyclePanel';
import {
  useNCREdit,
  NCRDetailTab,
  RootCauseTab,
  CorrectiveActionTab,
  DocumentsSection,
  CommentsSection,
  ActionLogSection,
  CloseNCRDialog,
  RejectCARDialog,
  RejectNCRDialog,
  ReopenNCRDialog,
} from './ncr-edit';
import { CollectionWorkflowTab } from './ncr-edit/CollectionWorkflowTab';
import { CreditWorkflowTab } from './ncr-edit/CreditWorkflowTab';
import { ReplacementWorkflowTab } from './ncr-edit/ReplacementWorkflowTab';
import { platformSettingsAPI, qualityAPI, QualityTabsVisibility } from '../lib/api';
import { toast } from 'sonner';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { WorkflowTabsList } from './ncr-edit/WorkflowTabsList';
import { eightDDefaultSubTab } from './ncr-edit/workflowTabs';
import { customerOwns8D, eightDOwnerLabel } from './ncr-edit/ownership';
import { useScrollTopOnLoad } from './ncr-edit/useScrollTopOnLoad';


export default function NCREdit() {
  const navigate = useNavigate();
  const {
    // Data
    ncr,
    documents,
    comments,
    actionLogs,
    formData,

    // UI states
    loading,
    saving,
    is8DRequired,
    newComment,
    closeDialogOpen,
    closeDialogMode,
    openCloseDialog,
    handleApprove8D,

    // Permissions
    isCustomer,
    isSupplier,
    canActAsCustomer,
    canEditStep1,
    canEditStep2,
    canEditStep3,
    canInputCAR,

    // Customer users
    customerUsers,
    warehouses,

    // Classification options
    sourcesOfRejection,
    commonisedActions,
    faultGroups,
    faultCategories,

    // CR-5999: per-field RCA validation errors surfaced inline next to each
    // required field on a failed Submit RCA attempt.
    rcaFieldErrors,

    // CR-6000: per-field CAR validation errors surfaced inline next to each
    // required field on a failed Submit CAR attempt.
    carFieldErrors,
    // CR-8706: fields a Save was refused on, highlighted in NCR Detail.
    detailFieldErrors,

    // Setters
    setNewComment,
    setCloseDialogOpen,
    // CR-6024
    rejectCARDialogOpen,
    setRejectCARDialogOpen,
    handleRejectCAR,
    // CR-6024 r3: reopen-closed-NCR
    reopenDialogOpen,
    setReopenDialogOpen,
    handleReopen,
    // Supplier reject-NCR + customer approve/decline
    rejectNCRDialogOpen,
    setRejectNCRDialogOpen,
    handleRejectNCR,
    handleApproveRejection,
    handleDeclineRejection,
    // 8D scoring: customer CAR grades captured with the approve/rework decision
    // CR-7357: four dimensions — ORC (rc), ERC, OPA (ca), EPA.
    rcScore,
    ercScore,
    caScore,
    epaScore,
    handleRcScoreChange,
    handleErcScoreChange,
    handleCaScoreChange,
    handleEpaScoreChange,
    updateFormData,

    // Actions
    refreshData,
    goBack,
    handleSave,
    handleSubmitDetail,
    handleSubmitRCA,
    handleSubmitCAR,
    handleAddComment,
    handleClose,

    // Multi-step workflow
    handleInitiateCollection,
    handleUpdateCollectionStatus,
    handleAssignCollection,
    handleInitiateCredit,
    handleSubmitCreditNote,
    handleUpdateCreditStatus,
    handleInitiateReplacement,
    handleSubmitReplacementDispatch,
    handleUpdateReplacementStatus,

    // Computed
    canClose,
  } = useNCREdit();

  // Schedule-lines tenant flag gates the delivery Warehouse field on the NCR
  // detail page. Reuses the established PlatformSettings.dashboard_tabs
  // 'scheduled' flag exposed via MenuSettingsContext — the same flag that
  // toggles the scheduled-deliveries dashboard tab. Warehouse on the GRN is
  // only meaningful for tenants receiving against scheduled deliveries.
  const { isDashboardTabEnabled, isEscapeRootCauseEnabledForCustomer } = useMenuSettings();
  const showWarehouse = isDashboardTabEnabled('scheduled');

  // CR-7359: "Download PDF". The server decides what this viewer may see, so
  // there is no client-side gate here beyond having the NCR open.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (!ncr) return;
    try {
      setDownloadingPdf(true);
      const response = await qualityAPI.downloadNCRPdf(ncr.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Prefer the name the server chose; fall back to the NCR number so a
      // blocked Content-Disposition header still yields a sensible file.
      const disposition = String(response.headers['content-disposition'] ?? '');
      const match = disposition.match(/filename="?([^";]+)"?/);
      link.download = match ? match[1] : `NCR_${ncr.ncr_number ?? ncr.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      console.error('Failed to download NCR PDF:', err);
      toast.error('Could not download the PDF. Try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Master Edit — an administrative override letting a customer admin amend an
  // NCR after it has left draft, at any workflow stage. Normal stage gating
  // (canEditStep1/2/3) is what a supplier and a rank-and-file customer user
  // get; this sits on top of it for admins only.
  //
  // The backend enforces the same rule and audits every changed field to the
  // NCR action log, so this toggle is an affordance, not the security boundary.
  const { user } = useAuth();
  const canMasterEdit =
    (isCustomer && !!user?.is_company_admin) || !!user?.is_staff || !!user?.is_superuser;
  const [masterEditing, setMasterEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Never leave the override armed on a draft — draft is already editable, so
  // the toggle would be meaningless and the banner misleading.
  const masterEditActive = canMasterEdit && masterEditing && ncr?.status !== 'draft';
  const saveWithMode = () => handleSave({ masterEdit: masterEditActive });

  // Tab visibility check
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // Check if NCRs tab is enabled
  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.quality_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabSettingsLoaded(true));
  }, []);

  // Redirect if NCRs tab is disabled
  useEffect(() => {
    if (!tabSettingsLoaded) return;
    if (!tabVisibility.ncrs) {
      navigate('/quality', { replace: true });
    }
  }, [tabSettingsLoaded, tabVisibility.ncrs, navigate]);

  useScrollTopOnLoad(loading ? undefined : ncr?.id);

  // Show loading while checking tab settings or loading NCR
  if (!tabSettingsLoaded || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (!ncr) {
    return (
      <div className="p-6">
        <div className="text-center">NCR not found</div>
      </div>
    );
  }

  const eightDSubTab = eightDDefaultSubTab(ncr.status, canEditStep3);
  // CR-7452: approving a rejection voids the NCR (deleted) but keeps
  // is_rejected, so the pending-rejection badge and review panel key off this.
  const rejectionPending = ncr.is_rejected && !['closed', 'deleted'].includes(ncr.status);
  // CR-8707: past draft, confirming containment is a change to the NCR like
  // any other, so it needs Master Edit (it was open to any customer user on an
  // open NCR under CR-7453).
  const canConfirmContainment = masterEditActive || (canActAsCustomer && ncr.status === 'draft');

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{ncr.ncr_number}</h1>
            {/* Was "NCR ID: {ncr.id}" — the database primary key. That is an
                  implementation detail: not stable across a restore, meaningless
                  in the customer's own systems, and its range hints at how many
                  NCRs exist platform-wide. Numbers are assigned on creation now,
                  so the heading above carries the identity. */}
              <p className="text-muted-foreground">Non-Conformance Report</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Spec PRINT NCR is the Download PDF below (Aaron, 30 Sep 2026: one
              NCR output, the server PDF). The browser print page is DORMANT. */}
          {/* Spec DELETE THIS NCR -- decision 2b (Aaron, 25 Sep 2026): the
              register's existing delete, same rules (customer admin, manually
              raised NCRs), now on the NCR page too. */}
          {canMasterEdit && ncr.created_manually && (
            <Button type="button" variant="outline" size="sm"
              className="text-red-600 hover:text-red-700" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
          {/* CR-6024 r3: customer-only Reopen affordance on a closed NCR. Spec
              acceptance criterion: "Closed NCRs can be reopened by customer
              users if required."
              r4: gate on canActAsCustomer (excludes Helix admin) so the button
              only renders when the backend will actually accept the action —
              Helix admins must Login As to reopen. */}
          {canActAsCustomer && ncr.status === 'closed' && (
            <Button type="button" variant="outline" size="sm" onClick={() => setReopenDialogOpen(true)}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reopen NCR
            </Button>
          )}
          {/* Supplier: reject a submitted (open) NCR with a free-text reason.
              Hidden once a rejection is already pending or the NCR is closed. */}
          {isSupplier
            && ['created', 'submitted', 'ca_submitted', 'car_submitted', 'eightd_complete'].includes(ncr.status)
            && !ncr.is_rejected && (
            <Button type="button" variant="destructive" size="sm" onClick={() => setRejectNCRDialogOpen(true)}>
              Reject NCR
            </Button>
          )}
          {rejectionPending && (
            <Badge variant="destructive">Rejection requested</Badge>
          )}
          {/* CR-7359: the whole NCR as a PDF. Offered to both sides — the
              server renders what the requester may see, so a supplier's copy
              simply omits internal comments. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleDownloadPdf()}
            disabled={downloadingPdf}
          >
            {downloadingPdf ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing…</>
            ) : (
              <><Download className="h-4 w-4 mr-2" />Download PDF</>
            )}
          </Button>
          <Badge variant={ncr.status === 'closed' ? 'default' : 'secondary'}>
            {ncr.status_display}
          </Badge>
          {/* Master Edit: only offered once the NCR has left draft, since a
              draft is already editable through the normal flow. */}
          {canMasterEdit && ncr.status !== 'draft' && (
            <Button
              type="button"
              variant={masterEditing ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setMasterEditing((v) => !v)}
            >
              {masterEditing ? (
                <><Unlock className="h-4 w-4 mr-2" />Master Edit On</>
              ) : (
                <><Lock className="h-4 w-4 mr-2" />Master Edit</>
              )}
            </Button>
          )}
        </div>
      </div>

      <NCRDeleteDialog ncr={ncr} open={deleteOpen} onOpenChange={setDeleteOpen}
        onDeleted={() => navigate('/quality')} />

      {masterEditActive && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">Master Edit is on</p>
          <p className="mt-1 text-sm text-amber-800">
            You are amending a submitted NCR as an administrator. Every field you
            change is recorded in the Workflow Action Log below, with the previous
            and new value against your name.
          </p>
        </div>
      )}

      {/* Customer: review a supplier's NCR rejection request. Approving voids
          the NCR (backend sets status → deleted) and records the reason in the
          audit history; declining lets the NCR continue. */}
      {canActAsCustomer && rejectionPending && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800">Supplier has rejected this NCR</p>
          <p className="mt-1 text-sm text-red-700 whitespace-pre-wrap">
            <span className="font-medium">Reason: </span>{ncr.rejection_reason || '—'}
          </p>
          <div className="mt-3 flex gap-2">
            <Button type="button" variant="destructive" size="sm" onClick={handleApproveRejection}>
              Accept &amp; Void NCR
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleDeclineRejection}>
              Decline
            </Button>
          </div>
        </div>
      )}

      {/* 24 Sep 2026: one lifecycle panel -- each required process with who
          acts next, then Close (the button appears exactly when the server
          will accept it). Replaces the 8D-only 4-step bar and the retired
          Template 2 strip. */}
      <NCRLifecyclePanel
        ncr={ncr}
        canActAsCustomer={canActAsCustomer}
        onCloseClick={() => openCloseDialog('close')}
      />

      {/* NCR Details - Always visible at top */}
      <NCRDetailTab
        ncr={ncr}
        formData={formData}
        canEdit={canEditStep1 || masterEditActive}
        canSubmit={canEditStep1}
        masterEdit={masterEditActive}
        saving={saving}
        customerUsers={customerUsers}
        sourcesOfRejection={sourcesOfRejection}
        faultGroups={faultGroups}
        faultCategories={faultCategories}
        showWarehouse={showWarehouse}
        warehouses={warehouses}
        canConfirmContainment={canConfirmContainment}
        fieldErrors={detailFieldErrors}
        onUpdateFormData={updateFormData}
        onSave={saveWithMode}
        onSubmitDetail={handleSubmitDetail}
        onCloseNo8D={() => openCloseDialog('close')}
      />

      {/* Workflow Tabs: 8D Report, Collection, Credit, Replacement */}
      <Tabs defaultValue="8d_report" className="space-y-4">
        <WorkflowTabsList ncr={ncr} />

        {/* CR-5981 rework: 8D-specific stepper below the 8D/Collection/Credit
            tab row — Template 2 only. Visualises the dynamic
            Liability / 8D / Collection / Credit progression with the
            "8D Completed" step gated on is_8d_required (mirrors the
            legacy "8D PROCESS" card). Template 1 has mandatory 8D and
            no separate 8D process card in legacy, so showing a second
            stepper here would just duplicate the one at the top. */}

        {/* 8D Report Tab — Root Cause + Corrective Action exposed as nested
            sub-tabs (customer request) instead of stacked sections.
            Default sub-tab follows the NCR workflow stage so the user lands
            on the section they're most likely to be working on:
              draft / ca_submitted → Root Cause (RCA in progress)
              car_submitted / closed → Corrective Action (RCA already done) */}
        <TabsContent value="8d_report" className="space-y-4">
          {!is8DRequired ? (
            // 8D Required = No: the supplier must not see the Root Cause
            // Analysis or Corrective & Preventive Action fields. Mirror the
            // Collection/Credit "not required" empty-state instead.
            <div className="rounded-lg border bg-card p-8 text-center">
              <FileWarning className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                An 8D report is not required for this NCR. Root cause analysis and
                corrective &amp; preventive actions are not needed.
              </p>
            </div>
          ) : (
          <Tabs
            // E9: open on the CAR when writing it is this viewer's next job; the
            // key re-opens the right sub-tab when a submit moves the stage on.
            key={eightDSubTab}
            defaultValue={eightDSubTab}
            className="space-y-4"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="root_cause">Root Cause Analysis</TabsTrigger>
              <TabsTrigger value="corrective_action">Corrective Action Report</TabsTrigger>
            </TabsList>
            <TabsContent value="root_cause">
              <RootCauseTab
                formData={formData}
                canEdit={canEditStep2 || masterEditActive}
                saving={saving}
                fieldErrors={rcaFieldErrors}
                // Master override: authorised internal (customer / Helix
                // admin) users may unlock and edit the supplier-only RCA
                // section through the portal. Saves via the standard NCR
                // PATCH (no workflow transition), which is permitted for
                // customer / helix users by the viewset's company scope.
                // The supplier's normal edit flow (canEdit) is unaffected.
                ownerLabel={eightDOwnerLabel(ncr)}
                escapeRootCauseEnabled={isEscapeRootCauseEnabledForCustomer(ncr.company)}
                graded={!customerOwns8D(ncr)}
                onUpdateFormData={updateFormData}
                onSave={saveWithMode}
                onSubmitRCA={handleSubmitRCA}
              />
            </TabsContent>
            <TabsContent value="corrective_action">
              <CorrectiveActionTab
                formData={formData}
                // Supplier can input/save the CAR alongside the RCA (status
                // 'created'); submitting the CAR still requires 'ca_submitted'.
                canEdit={canInputCAR || masterEditActive}
                canSubmit={canEditStep3}
                ownerLabel={eightDOwnerLabel(ncr)}
                // CR-7353: same Escape switch as the RCA tab — on splits the
                // CAR into Occurrence + Escape pairs, off shows the single pair.
                escapeRootCauseEnabled={isEscapeRootCauseEnabledForCustomer(ncr.company)}
                saving={saving}
                fieldErrors={carFieldErrors}
                onUpdateFormData={updateFormData}
                onSave={saveWithMode}
                onSubmitCAR={handleSubmitCAR}
                // CR-6024 r2: render the customer Approve / Reject & Rework
                // controls inside the CAR card (directly under the content the
                // customer is reviewing), per Jacq's rework note. canClose
                // already covers status==car_submitted + any collection/credit
                // gating.
                // DMR 71216: review cluster shows as soon as the CAR is
                // submitted — Reject & Rework must not wait for the claim
                // workflows; only Approve (which closes the NCR) is gated.
                showCustomerActions={canActAsCustomer && ncr.status === 'car_submitted'}
                canApprove={canClose}
                graded={!customerOwns8D(ncr)}
                onApproveClick={() => openCloseDialog('approve')}
                onRejectClick={() => setRejectCARDialogOpen(true)}
                commonisedActions={commonisedActions}
                rcScore={rcScore}
                ercScore={ercScore}
                caScore={caScore}
                epaScore={epaScore}
                onRcScoreChange={handleRcScoreChange}
                onErcScoreChange={handleErcScoreChange}
                onCaScoreChange={handleCaScoreChange}
                onEpaScoreChange={handleEpaScoreChange}
              />
            </TabsContent>
          </Tabs>
          )}
        </TabsContent>

        {/* Collection Tab */}
        <TabsContent value="collection">
          <CollectionWorkflowTab
            ncr={ncr}
            isCustomer={isCustomer}
            isSupplier={!isCustomer}
            saving={saving}
            onInitiate={handleInitiateCollection}
            onUpdateStatus={handleUpdateCollectionStatus}
            documents={documents.filter((doc) => doc.section === 'collection')}
            onDocumentsChanged={refreshData}
            onAssign={handleAssignCollection}
            currentUserId={user?.id}
            masterEditActive={masterEditActive}
          />
        </TabsContent>

        {/* Credit Tab */}
        <TabsContent value="credit">
          <CreditWorkflowTab
            ncr={ncr}
            isCustomer={isCustomer}
            isSupplier={!isCustomer}
            saving={saving}
            onInitiate={handleInitiateCredit}
            onSubmitNote={handleSubmitCreditNote}
            onUpdateStatus={handleUpdateCreditStatus}
          />
        </TabsContent>

        {/* Replacement Tab (mirrors Credit) */}
        <TabsContent value="replacement">
          <ReplacementWorkflowTab
            ncr={ncr}
            isCustomer={isCustomer}
            isSupplier={!isCustomer}
            saving={saving}
            onInitiate={handleInitiateReplacement}
            onSubmitDispatch={handleSubmitReplacementDispatch}
            onUpdateStatus={handleUpdateReplacementStatus}
          />
        </TabsContent>
      </Tabs>

      {/* Documents */}
      {/* B9: collection attachments live in the Collection card. */}
      <DocumentsSection ncrId={ncr.id} documents={documents.filter((doc) => !doc.section)} onDocumentsChanged={refreshData} />

      {/* Communications */}
      <CommentsSection
        comments={comments}
        newComment={newComment}
        onNewCommentChange={setNewComment}
        onAddComment={handleAddComment}
      />

      {/* Action Log */}
      <ActionLogSection actionLogs={actionLogs} />

      {/* CR-6024 r2: the Approve / Reject & Rework buttons that used to live
          here at the bottom of the page were moved up into CorrectiveActionTab
          so they sit directly under the CAR the customer is reviewing (Jacq
          rework note). Only the dialogs remain here. */}

      {/* Approve confirmation (closes the NCR) */}
      <CloseNCRDialog
        open={closeDialogOpen}
        onOpenChange={setCloseDialogOpen}
        mode={closeDialogMode}
        customerOwns8D={customerOwns8D(ncr)}
        onClose={closeDialogMode === 'approve_only'
          ? handleApprove8D : () => handleClose({ masterEdit: masterEditActive })}
        onKeepOpen={closeDialogMode === 'approve_and_close' ? handleApprove8D : undefined}
      />

      {/* CR-6024: Reject & Rework reason modal */}
      <RejectCARDialog
        open={rejectCARDialogOpen}
        customerOwns8D={customerOwns8D(ncr)}
        onOpenChange={setRejectCARDialogOpen}
        onReject={handleRejectCAR}
      />

      {/* CR-6024 r3: Reopen NCR confirmation modal */}
      <ReopenNCRDialog
        open={reopenDialogOpen}
        onOpenChange={setReopenDialogOpen}
        onReopen={handleReopen}
      />

      {/* Supplier reject-NCR reason modal */}
      <RejectNCRDialog
        open={rejectNCRDialogOpen}
        onOpenChange={setRejectNCRDialogOpen}
        onReject={handleRejectNCR}
      />
    </div>
  );
}
